<template>
  <d-modal
    @close="handleClose"
    size="lg"
    class="big-modal">
    <d-modal-header>
      <d-modal-title>
        Wybierz pracownia dla zadania "{{ processName }}"
      </d-modal-title>
    </d-modal-header>
    <d-modal-body class="p-0">
      <d-row class="px-4 py-3 w-50 mx-0">
        <d-input-group
          prepend="Sprawdź dostępność dla realizacji"
          append="sztuk produktu">
          <d-input
            v-model="necessaryQty"
            type="number"
            :min="0"
            :step="1"
            class="text-center" />
        </d-input-group>
      </d-row>

      <d-row class="px-4 pb-3 w-100 mx-0">
        <d-col
          cols="4"
          class="px-0">
          <d-input-group
            class="w-100"
            prepend="🔎">
            <d-input
              v-model="userFilter"
              type="search"
              class=""
              placeholder="Szukaj pracownika" />
          </d-input-group>
        </d-col>

        <d-col cols="3"></d-col>

        <d-col
          cols="5"
          class="d-flex pr-0 align-right">
          <d-input-group
            class="w-100 d-flex"
            prepend="Pokaż obłożenie w okresie:">
            <date-range-picker
              ref="picker"
              :locale-data="{
                'firstDay': 1,
                'format': 'DD-MM-YYYY',
                'daysOfWeek': ['Ndz', 'Pon', 'Wto', 'Śro', 'Czw', 'Ptk', 'Sob'],
                'monthNames': ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
              }"
              :singleDatePicker="false"
              :timePicker="false"
              :showWeekNumbers="false"
              :showDropdowns="false"
              :autoApply="true"
              v-model="range"
              :ranges="false"
              :linkedCalendars="true">
              <template v-slot:input="picker" style="min-width: 350px;">
                {{ picker.startDate | dateStart }} - {{ picker.endDate | dateEnd }}
              </template>
            </date-range-picker>
          </d-input-group>
        </d-col>
      </d-row>

      <d-row class="px-4 py-2 mb-3 w-100 mx-0 d-flex legend-wrapper">
        <span>Legenda:</span>
        <dl class="legend">
          <dt class="legend-green"></dt>
          <dd>dostępne moce przerobowe</dd>
          <dt class="legend-red"></dt>
          <dd>zajęte moce przerobowe</dd>
          <dt class="legend-blue"></dt>
          <dd>moce przerobowe dostępne dla wybranego zadania</dd>
        </dl>
      </d-row>

      <d-row :class="{
        'px-4 pb-3 w-100 mx-0': true,
        'is-loading': dataLoadInProgress
      }">
        <d-col
          cols="3"
          class="px-0 worker-names">
          <div class="worker-name-item-empty"></div>
          <div
            v-for="(worker, index) of filteredAndPreparedScheduleData"
            :key="'schedule-worker-' + index"
            :id="'worker-name-' + index"
            :class="{
              'worker-name-item px-2': true,
              'text-danger': !worker.capability
            }">
            <span class="d-block">
              {{ worker.name }}
            </span>
            <small
              v-if="worker.capability"
              class="d-block">
              ({{ Math.ceil(necessaryQty / worker.capability) }}
              <template v-if="Math.ceil(necessaryQty / worker.capability) > 1">dni</template>
              <template v-else>dzień</template>
               / {{ worker.capability }} szt na dzień)
            </small>
            <small v-else>
              (brak danych)
            </small>

            <d-tooltip
              v-if="!worker.capability"
              :target="'#worker-name-' + index"
              container=".worker-names">
              Brak przypisanych informacji o wydajności
            </d-tooltip>
          </div>
        </d-col>
        <d-col
          class="worker-days-wrapper px-0"
          cols="8">
          <div
            class="worker-days-row"
            :style="'--row-days-count: ' + datesInViewLabels.length + ';'">
            <div
              v-for="(date, index) of datesInViewLabels"
              :class="{
                'worker-days-item-heading': true,
                'is-free-day': isFreeDay[datesInView[index]]
              }"
              :key="'days-item-heading-' + index"
              v-pure-html="date">
            </div>
          </div>
          <div
            v-for="(worker, index) of filteredAndPreparedScheduleData"
            :key="'schedule-worker-days-' + index"
            class="worker-days-row"
            :id="'workers-day-row-' + index"
            :style="'--row-days-count: ' + datesInViewLabels.length + ';'">
            <div
              v-if="!worker.capability || !worker.days.length"
              class="worker-days-row-empty">
            </div>
            <template v-else>
              <div
                v-for="(day, subindex) of worker.days"
                :key="'schedule-worker-days-' + index + '-' + subindex"
                :class="{
                  'worker-days-day': true,
                  'is-free-day': isFreeDay[day.date]
                }"
                :id="'worker-day-' + index + '-' + subindex"
                :style="'--worker-day-occupancy: ' + (day.occupancy >= 100 ? 100 : day.occupancy) + '%; --worker-day-plan: ' + (day.planned_schedule ? day.planned_schedule.daily_percent : 0) + '%;'">
                <d-tooltip
                  :target="'#worker-day-' + index + '-' + subindex"
                  :container="'#workers-day-row-' + index">
                  Obłożenie: {{ day.occupancy }}%
                  <template v-if="day.planned_schedule && day.planned_schedule.quantity">
                    <br>
                    <strong>Możliwość realizacji:</strong>
                    <div>
                      {{ day.planned_schedule.quantity }} szt. z {{ necessaryQty }} szt.
                    </div>
                  </template>

                  <template v-if="day.schedules.length">
                    <br>
                    <strong>Zaplanowane prace tego dnia:</strong>
                    <div
                      v-for="(schedule, sindex) of day.schedules"
                      :key="'schedule-item-' + index + '-' + subindex + '-' + sindex">
                      {{ schedule.order_name }} (#{{ schedule.order_id }}) - {{ schedule.quantity }} szt.
                    </div>
                  </template>
                </d-tooltip>
              </div>
            </template>
          </div>
        </d-col>
        <d-col
          cols="1"
          class="px-0 text-center">
          <div class="worker-choose-item-empty"></div>
          <div
            v-for="(worker, index) of filteredAndPreparedScheduleData"
            :key="'schedule-worker-choose-' + index"
            class="worker-choose-item pl-2">
            <d-button
              theme="success"
              @click.prevent="assignWorker(worker.id)">
              Wybierz
            </d-button>
          </div>
        </d-col>
        <d-alert
          v-if="!filteredAndPreparedScheduleData || !filteredAndPreparedScheduleData.length"
          :show="true"
          theme="light"
          class="text-center w-100">
          Brak pracowników pasujacych do podanych kryteriów&hellip;
        </d-alert>
      </d-row>
    </d-modal-body>
  </d-modal>
</template>

<script>
import debounce from 'lodash.debounce';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import FormUtils from './../../../utils/FormUtils.js';

export default {
  name: 'order-schedule-popup',
  components: {
    'date-range-picker': DateRangePicker
  },
  props: [
    'processName',
    'productIndex',
    'processIndex',
    'processID',
    'processStartDate',
    'qty'
  ],
  filters: {
    dateStart (input) {
      if (input === null) {
        return ' od ';
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    dateEnd (input) {
      if (input === null) {
        return ' do ';
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    }
  },
  watch: {
    'necessaryQty': function () {
      if (this.initialLoadDone) {
        this.debouncedFetchData();
      }
    },
    'range.startDate': function () {
      if (this.initialLoadDone) {
        this.debouncedFetchData();
      }
    },
    'range.endDate': function () {
      if (this.initialLoadDone) {
        this.debouncedFetchData();
      }
    }
  },
  computed: {
    filteredAndPreparedScheduleData () {
      let dataToPrepare = JSON.parse(JSON.stringify(this.scheduleData));

      if (this.userFilter) {
        dataToPrepare = dataToPrepare.filter(row => row.name.toLocaleLowerCase().indexOf(this.userFilter.toLocaleLowerCase()) > -1);
      }

      return dataToPrepare;
    },
    isFreeDay () {
      let freeDayList = {};

      for (let i = 0; i < this.datesInView.length; i++) {
        let dayIndex = this.datesInView[i];
        let dayNumber = new Date(this.datesInView[i]).getDay();
        freeDayList[dayIndex] = dayNumber === 0 || dayNumber === 6;
      }

      return freeDayList;
    }
  },
  data () {
    return {
      dataLoadInProgress: false,
      necessaryQty: 0,
      userFilter: '',
      datesInView: [],
      datesInViewLabels: [],
      range: {
        startDate: null,
        endDate: null
      },
      scheduleData: [],
      initialLoadDone: false
    };
  },
  mounted () {
    this.loadInitialData();
  },
  methods: {
    loadInitialData () {
      this.dataLoadInProgress = true;
      this.necessaryQty = this.qty;
      let startDate = this.processStartDate;
      let endDate;

      if (!startDate) {
        startDate = new Date().toISOString().split('T')[0];
      }

      endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 30);
      endDate = endDate.toISOString().split('T')[0];

      this.range.startDate = new Date(startDate);
      this.range.endDate = new Date(endDate);
      this.datesInView = this.generateDatesRange(this.range.startDate, this.range.endDate);
      this.datesInViewLabels = this.generateDatesRange(this.range.startDate, this.range.endDate, true);

      FormUtils.submitInBackground(this, {
        endpoint: '/orders/schedules/items-for-users?date_from=' + startDate + '&date_to=' + endDate + '&quantity=' + this.necessaryQty + '&process_id=' + this.processID,
        errorTitle: 'Wystąpił błąd',
        errorText: 'Nie udało się wczytać danych harmonogramu. Spróbuj ponownie',
        successAction: (data) => {
          this.scheduleData = data.data;
          this.dataLoadInProgress = false;
          this.initialLoadDone = true;
        },
        failAction: () => {
          this.dataLoadInProgress = false;
        }
      });
    },
    loadData () {
      this.dataLoadInProgress = true;
      let startDate = new Date(this.range.startDate).toISOString().split('T')[0];
      let endDate = new Date(this.range.endDate).toISOString().split('T')[0];
      this.datesInView = this.generateDatesRange(this.range.startDate, this.range.endDate);
      this.datesInViewLabels = this.generateDatesRange(this.range.startDate, this.range.endDate, true);

      FormUtils.submitInBackground(this, {
        endpoint: '/orders/schedules/items-for-users?date_from=' + startDate + '&date_to=' + endDate + '&quantity=' + this.necessaryQty + '&process_id=' + this.processID,
        errorTitle: 'Wystąpił błąd',
        errorText: 'Nie udało się wczytać danych harmonogramu. Spróbuj ponownie',
        successAction: (data) => {
          this.scheduleData = data.data;
          this.dataLoadInProgress = false;
        },
        failAction: () => {
          this.dataLoadInProgress = false;
        }
      });
    },
    // eslint-disable-next-line
    debouncedFetchData: debounce(function () {
      this.loadData();
    }, 1000),
    assignWorker (workerID) {
      this.$bus.$emit('order-product-assign-worker', this.productIndex, this.processIndex, workerID, this.necessaryQty);
      this.handleClose();
    },
    generateDatesRange (startDate, endDate, htmlView = false) {
      let dates = [];
      let currentDate = new Date(startDate.getTime());

      startDate.setUTCHours(0, 0, 0, 0);
      endDate.setUTCHours(0, 0, 0, 0);

      while (currentDate <= endDate) {
        let year = currentDate.getFullYear();
        let month = String(currentDate.getMonth() + 1).padStart(2, '0');
        let day = String(currentDate.getDate()).padStart(2, '0');

        if (htmlView) {
          let monthName = '';

          switch (month) {
            case '01': monthName = 'STY'; break;
            case '02': monthName = 'LUT'; break;
            case '03': monthName = 'MAR'; break;
            case '04': monthName = 'KWI'; break;
            case '05': monthName = 'MAJ'; break;
            case '06': monthName = 'CZW'; break;
            case '07': monthName = 'LIP'; break;
            case '08': monthName = 'SIE'; break;
            case '09': monthName = 'WRZ'; break;
            case '10': monthName = 'PAŹ'; break;
            case '11': monthName = 'LIS'; break;
            case '12': monthName = 'GRU'; break;
            default: monthName = '???'; break;
          }

          dates.push(`<strong>${day}</strong><span>${monthName}</span><small>${year}<small>`);
        } else {
          dates.push(`${year}-${month}-${day}`);
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      return dates;
    },
    handleClose () {
      this.$bus.$emit('close-order-schedule-popup');
    }
  }
}
</script>

<style lang="scss" scoped>
.big-modal {
  /deep/ .modal-lg {
    max-width: 1240px;
    width: 100%;
  }

  /deep/ .input-group-text {
    color: #555;
  }

  .worker {
    &-days-wrapper {
      overflow: auto;
      padding-bottom: 30px;
      width: 100%;
    }

    &-names {
      position: relative;
    }

    &-days-row {
      display: flex;
      width: calc(var(--row-days-count) * 50px);

      &-empty {
        background: #f0f0f0;
        border: 1px solid #ddd;
        border-top: none;
        height: 45px;
        width: calc(var(--row-days-count) * 50px);
      }
    }

    &-days-day {
      background: #17c671;
      border-right: 1px solid #ddd;
      border-bottom: 1px solid #ddd;
      cursor: help;
      height: 45px;
      position: relative;
      width: 50px;

      &:first-child {
        border-left: 1px solid #ddd;
      }

      &:after {
        background: #c4183c;
        bottom: 0;
        content: "";
        height: var(--worker-day-occupancy);
        left: 0;
        position: absolute;
        width: 100%;
      }

      &:before {
        background: #007bff;
        bottom: var(--worker-day-occupancy);
        content: "";
        height: var(--worker-day-plan);
        left: 0;
        position: absolute;
        width: 100%;
      }
    }

    &-name-item {
      border-bottom: 1px solid #ddd;
      border-left: 1px solid #ddd;
      border-right: 1px solid #ddd;
      display: flex;
      flex-direction: column;
      height: 45px;
      justify-content: center;

      &-empty {
        border-bottom: 1px solid #ddd;
        border-right: 1px solid #ddd;
        height: 50px;
      }
    }

    &-days-item {
      height: 45px;

      &-heading {
        border-top: 1px solid #ddd;
        border-right: 1px solid #ddd;
        border-bottom: 1px solid #ddd;
        display: flex;
        flex-direction: column;
        height: 50px;
        justify-content: center;
        text-align: center;
        width: 50px;

        &:first-child {
          border-left: 1px solid #ddd;
        }

        /deep/ strong {
          display: block;
          font-size: 20px;
          line-height: 1;
        }

        /deep/ span {
          display: block;
          font-size: 14px;
          font-weight: 300;
          letter-spacing: 1px;
          line-height: .85;
        }

        /deep/ small {
          display: block;
          font-size: 10px;
          line-height: 1.2;
          opacity: .5;
        }
      }
    }

    &-choose-item {
      align-items: center;
      border-bottom: 1px solid #ddd;
      border-left: 1px solid #ddd;
      border-right: 1px solid #ddd;
      display: flex;
      height: 45px;

      &-empty {
        border-bottom: 1px solid #ddd;
        border-left: 1px solid #ddd;
        height: 50px;
      }
    }
  }

  .is-loading {
    opacity: .25;
    pointer-events: none;
  }

  .align-right {
    justify-content: flex-end;
  }

  .vue-daterange-picker {
    width: 290px;
  }

  .is-free-day {
    opacity: .5;
  }

  .legend {
    align-items: center;
    display: flex;
    font-weight: 300;
    margin: 0;

    dt {
      margin-left: 20px;
      margin-right: 5px;
    }

    dd {
      margin: 0;
    }

    &-wrapper {
      background: #f5f5f5;
      border-bottom: 1px solid #ddd;
      border-top: 1px solid #ddd;
      justify-content: center;
    }

    &-green {
      background: #17c671;
      border-radius: 3px;
      height: 16px;
      position: relative;
      top: 1px;
      width: 16px;
    }

    &-red {
      background: #c4183c;
      border-radius: 3px;
      height: 16px;
      position: relative;
      top: 1px;
      width: 16px;
    }

    &-blue {
      background: #007bff;
      border-radius: 3px;
      height: 16px;
      position: relative;
      top: 1px;
      width: 16px;
    }
  }
}
</style>
